import { TypeAnimation } from 'react-type-animation'
import { createRef, useEffect, useState, useContext } from 'react'
import styled, { useTheme } from 'styled-components'
import { SourceArticles } from './SourceArticles'
import { MdOutlineArrowOutward } from 'react-icons/md'
import { TrackingContext } from 'contexts'

// import {
//   getArticleTeaserData,
//   ArticleData,
// } from '@ta-interaktiv/react-article-teasers'

// import Rating from '../Rating'
import { Fragment } from 'react'
import { marked } from 'marked'
import { getArticleTeaserData } from './getArticleTeaserData'

const formatMessage = (message: ChatInterface) => {
  const paragraphs = message.text.split('\n')

  return paragraphs.map((p, index) => (
    <p key={`message-${message.id}-paragraph-${index}`}>{p}</p>
  ))
}

interface ParsedElement {
  key: string
  type: string
  content: string | ParsedElement[]
  href?: string
}

// Recursively parses the HTML elements and their
// children into a structured format
const parseChildren = (
  element: Element,
  key: string,
  depth: number[] = []
): string | ParsedElement[] => {
  const children = Array.from(element.childNodes)

  // If there are no children, return the text content
  if (children.length === 0) {
    return element.textContent ? element.textContent.trim() : ''
  }

  // Map each child to its parsed representation
  const content = children
    .map((child, index): ParsedElement | null => {
      const newDepth = [...depth, index + 1]
      const uniqueKey = `${key}-chunk-${newDepth.join('-')}`

      // If the child is an element, continue to recursively parse it
      if (child.nodeType === Node.ELEMENT_NODE && child instanceof Element) {
        return {
          key: uniqueKey,
          type: child.tagName.toLowerCase(),
          content: parseChildren(child, key, newDepth),
          href: child.getAttribute('href') || undefined,
        }
        // If the child is a text node, return its content
      } else if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ? child.textContent : ''
        if (text === '') return null
        return {
          key: uniqueKey,
          type: 'text',
          content: text,
        }
      }
      return null
    })
    // Remove null and empty elements
    .filter(
      (item): item is ParsedElement => item !== null && item.content !== ''
    )

  return content
}

// Get all the children of the body element and parse them
const parseHtml = (message: ChatInterface): ParsedElement[] => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(
    marked(message.text) as string,
    'text/html'
  )

  return parseChildren(doc.body, `message-${message.id}`) as ParsedElement[]
}

const DrawStringElement = ({
  element,
  handleElementDrawn,
}: {
  element: ParsedElement
  handleElementDrawn: () => void
}) => {
  const ref = createRef<HTMLSpanElement>()
  const [showCursor, setShowCursor] = useState<boolean>(true)

  return (
    <StyledTypeAnimation
      showCursor={showCursor}
      ref={ref}
      key={element.key}
      sequence={[
        element.content as string,
        () => {
          setShowCursor(false)
          handleElementDrawn()
        },
      ]}
      speed={99}
      cursor={false}
    />
  )
}

// Recursively draw the parsed elements. We need to go
// down the tree until we reach the text elements
const DrawParsedElement = ({
  element,
  handleElementDrawn,
}: {
  element: ParsedElement
  handleElementDrawn: () => void
}) => {
  const [elementIndex, setElementIndex] = useState(1)
  const ElementType = element.type as keyof JSX.IntrinsicElements
  const trackEvent = useContext(TrackingContext)
  const tenantId = useTheme().tenant.key
  const articleId = element.href ? element.href.split('/').pop() : null

  useEffect(() => {
    // Reset the index if the element type changes
    setElementIndex(1)
  }, [element.type])

  const h = () => {
    setElementIndex((prevIndex) => prevIndex + 1)
    if (elementIndex === (element.content as ParsedElement[]).length) {
      handleElementDrawn()
    }
  }

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    // Track the click event
    trackEvent &&
      trackEvent('teaser_click', {
        teaser_article_url: `https://www.${tenantId}.ch/${articleId}`,
        teaser_source: 'streaming_search_ai',
      })

    window.open(`https://www.${tenantId}.ch/${articleId}`, '_blank')
  }

  const RenderElement = ElementType === 'a' ? StyledAnchor : element.type
  const dynamicAttributes =
    ElementType === 'a'
      ? {
          href: `https://www.${tenantId}.ch/${articleId}`,
          target: '_blank',
          onClick: handleLinkClick,
        }
      : {}

  return (
    <>
      {/* @ts-ignore */}
      <RenderElement {...dynamicAttributes}>
        {(element.content as ParsedElement[])
          .slice(0, elementIndex)
          .map((child, index) => {
            return (
              <Fragment key={index}>
                <>
                  {child.type === 'text' ? (
                    <DrawStringElement element={child} handleElementDrawn={h} />
                  ) : (
                    <DrawParsedElement element={child} handleElementDrawn={h} />
                  )}
                  {child.type === 'a' ? <MdOutlineArrowOutward /> : null}
                </>
              </Fragment>
            )
          })}
      </RenderElement>
    </>
  )
}

const DrawElement = ({
  element,
  handleElementDrawn,
}: {
  element: ParsedElement
  handleElementDrawn: () => void
}) => {
  return element.type === 'text' ? (
    <DrawStringElement
      element={element}
      handleElementDrawn={handleElementDrawn}
    />
  ) : (
    <DrawParsedElement
      element={element}
      handleElementDrawn={handleElementDrawn}
    />
  )
}

const Message = ({ message }: { message: ChatInterface }) => {
  const [showArticles, setShowArticles] = useState(false)
  const [elementIndex, setElementIndex] = useState(1)
  const [articles, setArticles] = useState(new Map())
  const theme = useTheme()
  // We need to parse the HTML content into elements that can
  // be animated one after the other to create a typing effect
  const elements = parseHtml(message)

  const handleEndWriting = () => {
    setTimeout(() => {
      setShowArticles(true)
    }, 350)
  }

  // Fetch article teaser data for each article
  useEffect(() => {
    if (message.articles && message.articles.length > 0) {
      message.articles.forEach((article) => {
        getArticleTeaserData(theme.tenant.key, article).then(
          (articleTeaserData: any) => {
            if (articleTeaserData) {
              setArticles(
                (articles) => new Map(articles.set(article, articleTeaserData))
              )
            }
          }
        )
      })
    }
  }, [])

  // After an element has been drawn, we need to draw the next one
  const handleElementDrawn = () => {
    if (elementIndex === elements.length) {
      handleEndWriting()
    } else {
      setElementIndex((prevIndex) => prevIndex + 1)
    }
  }

  return (
    <Container type={message.type}>
      {message.type === 'bot' ? (
        <>
          <TextAuthor>
            <img
              style={{ width: '50px' }}
              src={require(`../logo-${theme.tenant.key}-${theme.colorMode}.svg`)}
              alt={theme.tenant.key}
            />
          </TextAuthor>
          <div>
            <Text type={message.type}>
              <>
                {elements.slice(0, elementIndex).map((element) => {
                  return (
                    <DrawElement
                      key={element.key}
                      element={element}
                      handleElementDrawn={handleElementDrawn}
                    />
                  )
                })}
              </>
            </Text>
            {showArticles && articles.size > 0 && (
              <SourceArticles articles={articles} />
            )}
          </div>
        </>
      ) : (
        <>
          <Text type={message.type}>{formatMessage(message)}</Text>
        </>
      )}
    </Container>
  )
}

const StyledAnchor = styled.a`
  -webkit-text-decoration: none;
  text-decoration: none;
  padding: 0.2rem 0 0;
  background-image: linear-gradient(
    to right,
    var(--yellow-main) 0,
    var(--yellow-main) 100%
  );
  background-size: 100% 20%;
  background-repeat: repeat-x;
  color: ${({ theme }) => theme.colorText};
  background-position: bottom;

  &:hover {
    background-size: 100% 100%;
  }
`

const cursorAnimation = `
  &::after {
    content: '';
    width: 1.15rem;
    height: 2rem;
    background-color: ${({ theme }: { theme: any }) => theme.colorText};
    position: relative;
    display: inline-block;
    animation: cursor 500ms infinite step-start;
    transition: opacity 0.5s ease-in-out;
    opacity: 0.65;
  }

  @keyframes cursor {
    50% {
      opacity: 0;
    }
  }
`

const Text = styled.div<{ type: 'bot' | 'user' }>`
  padding: 1rem 1.5rem;
  position: relative;
  line-height: 1.4;
  font-size: 1.6rem;
  -webkit-font-smoothing: antialiased;
  border-color: ${({ theme }) => theme.colorGrey};
  border-style: solid;
  border-width: 1px;
  border-radius: 2rem;
  background: var(--site-background);
  color: ${({ theme }) => theme.colorText};

  p:first-child {
    margin-top: 0;
  }
  p:last-of-type {
    margin-bottom: 0;
  }

  li p {
    margin: 1em 0;
  }

  li {
    margin-bottom: 1em;
  }

  li:last-child {
    margin-bottom: 0;
  }
`

const TextAuthor = styled.div`
  font-weight: bold;
  font-size: 1.5rem;
  margin-right: 1.5rem;
`

const Container = styled.div<{ type: 'bot' | 'user' }>`
  position: relative;
  line-height: 1.4;
  font-size: 1.6rem;
  -webkit-font-smoothing: antialiased;
  margin: 0rem 0 4rem 0;
  display: ${({ type, theme }) => (type === 'bot' ? 'flex' : 'inline-block')};
  text-align: left;
`

const StyledTypeAnimation = styled(TypeAnimation)<{ showCursor: boolean }>`
  ${({ showCursor }) => showCursor && cursorAnimation}
`

// const RatingContainer = styled.div`
//   text-align: right;
//   margin-top: 1rem;
//   margin-bottom: 1rem;
// `

export default Message
